import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Gamepad2Icon } from "lucide-react";
import { useState } from "react";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { TeamNames } from "@/components/teams/names";
import { useTRPC } from "@/integrations/trpc/react";
import { type MatchIdProps, useMatchTeams } from "@/lib/matches";
import { invalidateMatchQueries } from "@/lib/tournament-cache";

import type { MatchTeam } from "../panels/games/pool-match-grid";

export type SimulateMatchModalProps = MatchIdProps & {
  tournamentDivisionId: number;
  opponent?: MatchTeam | null;
};

export function SimulateMatchModal(props: SimulateMatchModalProps) {
  const [isOpen, setOpen] = useState(false);

  return (
    <>
      <Button
        size="xs"
        variant="text"
        className="self-end text-green-500 hover:text-green-600"
        onPress={() => setOpen(true)}
        tooltip="Simulate match"
      >
        <Gamepad2Icon size={16} />
      </Button>

      {isOpen && (
        <SimulateMatchModalContent
          {...props}
          isOpen={isOpen}
          onOpenChange={setOpen}
        />
      )}
    </>
  );
}

function SimulateMatchModalContent({
  poolMatchId,
  playoffMatchId,
  isOpen,
  onOpenChange,
}: SimulateMatchModalProps & {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const teams = useMatchTeams({ poolMatchId, playoffMatchId });

  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { mutate, failureReason, isPending } = useMutation({
    ...trpc.tournaments.simulateMatch.mutationOptions(),
    onSuccess: () => {
      invalidateMatchQueries(queryClient, trpc);
      onOpenChange(false);
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Simulate Match
        </Heading>

        {teams?.teamA && teams?.teamB && (
          <p>
            Are you sure you want to simulate match between{" "}
            <TeamNames
              className="font-semibold italic"
              players={teams?.teamA?.team.players}
              link={false}
            />{" "}
            and{" "}
            <TeamNames
              className="font-semibold italic"
              players={teams?.teamB?.team.players}
              link={false}
            />
            ?
          </p>
        )}

        {failureReason && (
          <p className="text-sm text-red-600">{failureReason.message}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="solid"
            color="primary"
            isDisabled={isPending}
            onPress={() => mutate({ poolMatchId, playoffMatchId })}
          >
            {isPending ? "Simulating…" : "Confirm"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
